import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, FileText, Folder, MoreHorizontal } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useFeedbacksFetcher, FeedbackData } from '@/hooks/useFeedbacksFetcher';

interface ProjectInfo {
  id: string;
  name: string;
}

interface FeedbackWithProject extends FeedbackData {
  project?: ProjectInfo | null;
}

interface ReceivedFeedbacksProps {
  isCandidat?: boolean;
  isEntreprise?: boolean;
}

export const ReceivedFeedbacks: React.FC<ReceivedFeedbacksProps> = ({ isCandidat, isEntreprise }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { feedbacks, loading, refetch } = useFeedbacksFetcher();
  const [feedbacksWithProjects, setFeedbacksWithProjects] = useState<FeedbackWithProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    const loadProjectsInfo = async () => {
      if (!feedbacks.length || !user) return;
      
      setLoadingProjects(true);
      try {
        const feedbacksWithProjectsData = await Promise.all(
          feedbacks.map(async (feedback) => {
            try {
              const { data, error } = await supabase
                .from('projects_feedbacks')
                .select(`
                  project_id,
                  projects (id, name)
                `)
                .eq('feedback_id', feedback.id)
                .eq('user_id', user.id)
                .maybeSingle();
              
              if (error) throw error;
              
              return {
                ...feedback,
                project: data && data.projects ? {
                  id: data.project_id,
                  name: data.projects.name
                } : null
              };
            } catch (error) {
              console.error(`Error loading project for feedback ${feedback.id}:`, error);
              return { ...feedback, project: null };
            }
          })
        );
        
        setFeedbacksWithProjects(feedbacksWithProjectsData);
      } catch (error) {
        console.error('Error loading projects info:', error);
        setFeedbacksWithProjects(feedbacks.map(feedback => ({ ...feedback, project: null })));
      } finally {
        setLoadingProjects(false);
      }
    };
    
    loadProjectsInfo();
  }, [feedbacks, user]);

  const handleViewFeedback = (feedbackId: string, feedbackType?: string | null) => {
    navigate(`/view-feedback/${feedbackId}`, { 
      state: { feedbackId, feedbackType } 
    });
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const handleViewProject = (projectId: string) => {
    if (projectId) {
      navigate(`/projects/${projectId}`);
    }
  };

  const handleDeleteFeedback = async (feedbackId: string) => {
    if (!user) return;
    
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce feedback ? Cette action est irréversible.")) {
      try {
        const { error: projectLinkError } = await supabase
          .from('projects_feedbacks')
          .delete()
          .eq('feedback_id', feedbackId)
          .eq('user_id', user.id);
        
        if (projectLinkError) {
          throw projectLinkError;
        }
        
        const { error: deleteError } = await supabase
          .from('feedbacks')
          .delete()
          .eq('id', feedbackId)
          .eq('user_id', user.id);
        
        if (deleteError) {
          throw deleteError;
        }
        
        toast({
          title: "Feedback supprimé",
          description: "Le feedback a été supprimé avec succès.",
        });
        
        refetch();
      } catch (error: any) {
        console.error('Erreur lors de la suppression du feedback:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer le feedback. " + error.message,
          variant: "destructive",
        });
      }
    }
  };

  const handleArchiveFeedback = async (feedbackId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('feedbacks')
        .update({ status: 'archived' })
        .eq('id', feedbackId)
        .eq('user_id', user.id);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Feedback archivé",
        description: "Le feedback a été archivé avec succès.",
      });
      
      refetch();
    } catch (error: any) {
      console.error('Erreur lors de l\'archivage du feedback:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'archiver le feedback. " + error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((_, i) => (
          <Card key={i} className="p-4">
            <Skeleton className="h-6 w-1/4 mb-2" />
            <Skeleton className="h-4 w-3/4 mb-1" />
            <Skeleton className="h-4 w-1/2" />
          </Card>
        ))}
      </div>
    );
  }

  if (feedbacksWithProjects.length === 0) {
    return (
      <Card className="p-4 text-center">
        <p className="mb-4">Vous n'avez pas encore reçu de feedback.</p>
        <Button onClick={() => navigate('/request')}>
          Demander un feedback
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {feedbacksWithProjects.map((feedback) => (
        <Card key={feedback.id} className="overflow-hidden">
          <div className="p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-lg">
                    {feedback.candidate_name}
                  </h3>
                  <Badge variant="outline" className="text-xs">
                    {feedback.feedback_type === 'candidat' ? 'Feedback Candidat' : 'Feedback Recruteur'}
                  </Badge>
                  {feedback.certified && (
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      Certifié
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">{feedback.job_title}</p>
              </div>

              <div className="flex flex-col items-end">
                <div className="text-sm text-gray-500">
                  {formatDate(feedback.created_at)}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => handleViewFeedback(feedback.id, feedback.feedback_type)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Voir le feedback
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleArchiveFeedback(feedback.id)}>
                      <FileText className="mr-2 h-4 w-4" />
                      Archiver
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDeleteFeedback(feedback.id)}
                      className="text-red-600">
                      <FileText className="mr-2 h-4 w-4" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {feedback.project && (
              <div className="mt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleViewProject(feedback.project!.id)}
                  className="flex items-center gap-1 text-xs"
                >
                  <Folder className="h-3 w-3" />
                  <span>Projet: {feedback.project.name}</span>
                </Button>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <Button 
                size="sm" 
                onClick={() => handleViewFeedback(feedback.id, feedback.feedback_type)}
              >
                Voir le feedback
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default ReceivedFeedbacks;
